/**
 * TodoAddSubtaskRow - Inline input row for adding new subtasks to a task.
 * Shows a "+" icon that expands into a text input when pressed.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Pressable, StyleSheet, TextInput, View } from 'react-native';
import * as Haptics from 'expo-haptics';
import { Ionicons } from '@expo/vector-icons';

import { Spacing, BorderRadius } from '@/constants/spacing';
import { useTodoScroll } from './TodoScrollContext';

// Sticky note design constants
const STICKY_CHECKBOX_COLOR = '#666666';
const STICKY_TEXT_COLOR = '#333333';

type TodoAddSubtaskRowProps = {
  onAddSubtask: (title: string) => void;
  onEditingChange?: (isEditing: boolean) => void;
};

export function TodoAddSubtaskRow({ onAddSubtask, onEditingChange }: TodoAddSubtaskRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<TextInput>(null);
  const rowRef = useRef<View>(null);
  const justSubmittedRef = useRef(false);
  // Mirror the latest input + add callback so unmount cleanup can save without
  // depending on stale closures or React state being current.
  const inputValueRef = useRef('');
  const onAddSubtaskRef = useRef(onAddSubtask);
  const isEditingRef = useRef(false);
  const { scrollToInput, setIsEditing: setContextEditing } = useTodoScroll();

  useEffect(() => {
    inputValueRef.current = inputValue;
  }, [inputValue]);

  useEffect(() => {
    onAddSubtaskRef.current = onAddSubtask;
  }, [onAddSubtask]);

  useEffect(() => {
    isEditingRef.current = isEditing;
  }, [isEditing]);

  // Persist any pending text if the row is unmounted while still editing
  // (e.g. parent re-renders and hides the "+ Add" UI before onBlur fires).
  useEffect(() => {
    return () => {
      if (!isEditingRef.current) return;
      const pending = inputValueRef.current.trim();
      if (pending && !justSubmittedRef.current) {
        onAddSubtaskRef.current?.(pending);
      }
    };
  }, []);

  const handlePressAdd = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setIsEditing(true);
    setContextEditing(true);
    onEditingChange?.(true);
    setTimeout(() => scrollToInput(rowRef), 50);
  }, [scrollToInput, setContextEditing, onEditingChange]);

  const handleSubmit = useCallback(() => {
    const trimmed = inputValue.trim();
    if (trimmed) {
      // Has content: save subtask, clear input, keep keyboard open
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      onAddSubtask(trimmed);
      setInputValue('');
      inputValueRef.current = '';
      justSubmittedRef.current = true;
      setTimeout(() => scrollToInput(rowRef), 50);
    } else if (justSubmittedRef.current) {
      // Empty input after just submitting: close and dismiss keyboard
      setIsEditing(false);
      setContextEditing(false);
      onEditingChange?.(false);
      inputRef.current?.blur();
      justSubmittedRef.current = false;
    }
    // Empty input without prior submit: do nothing, stay open
  }, [inputValue, onAddSubtask, setContextEditing, scrollToInput, onEditingChange]);

  const handleBlur = useCallback(() => {
    const trimmed = inputValueRef.current.trim();
    if (trimmed && !justSubmittedRef.current) {
      onAddSubtask(trimmed);
      setInputValue('');
      inputValueRef.current = '';
    }
    setIsEditing(false);
    setContextEditing(false);
    onEditingChange?.(false);
    justSubmittedRef.current = false;
  }, [onAddSubtask, setContextEditing, onEditingChange]);

  if (!isEditing) {
    return (
      <Pressable style={styles.addButton} onPress={handlePressAdd} hitSlop={12}>
        <Ionicons name="add" size={20} color={STICKY_CHECKBOX_COLOR} />
      </Pressable>
    );
  }

  return (
    <View ref={rowRef} style={styles.inputRow}>
      <View style={styles.inputCheckbox} />
      <TextInput
        ref={inputRef}
        style={styles.input}
        value={inputValue}
        onChangeText={setInputValue}
        onSubmitEditing={handleSubmit}
        onBlur={handleBlur}
        placeholder="Add subtask"
        placeholderTextColor={STICKY_CHECKBOX_COLOR}
        autoFocus
        returnKeyType="done"
        blurOnSubmit={false}
      />
    </View>
  );
}

// Center 16px subtask checkbox under 24px main checkbox: (24-16)/2 = 4px offset
const CHECKBOX_ALIGNMENT_OFFSET = 4;
// Center 20px + icon under 16px subtask checkbox: 4 - (20-16)/2 = 2px offset
const ADD_BUTTON_ALIGNMENT_OFFSET = 2;

const styles = StyleSheet.create({
  addButton: {
    alignSelf: 'flex-start',
    paddingVertical: Spacing.xs,
    marginTop: Spacing.sm,
    marginLeft: ADD_BUTTON_ALIGNMENT_OFFSET,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: Spacing.xs,
    marginTop: Spacing.sm,
    paddingLeft: CHECKBOX_ALIGNMENT_OFFSET,
    gap: Spacing.md + 2, // 14px to match subtask rows
  },
  inputCheckbox: {
    width: 16,
    height: 16,
    borderRadius: BorderRadius.xs,
    borderWidth: 1.5,
    borderColor: STICKY_CHECKBOX_COLOR,
  },
  input: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    color: STICKY_TEXT_COLOR,
    padding: 0,
  },
});
